import { getFileUrl } from '@/clients/http'
import { Image as ImageIcon, Plus, X } from 'lucide-react'

import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { ProductModel } from '@/types/model/Product'

interface MultiImageUploaderProps {
  value: ProductModel.ProductMedia[]
  onChange: (images: ProductModel.ProductMedia[]) => void
  maxImages?: number
  label?: string
  description?: string
  id?: string
}

export const MultiImageUploader = ({
  value = [],
  onChange,
  maxImages = 5,
  label,
  description,
  id,
}: MultiImageUploaderProps) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)

  const handleSelectImages = (selectedFiles: any[]) => {
    const currentImageIds = new Set(value.map((img) => img.id))
    const newUniqueFiles = selectedFiles
      .filter((file) => !currentImageIds.has(file.id))
      .map((file) => ({ id: file.id, url: file.url }))

    onChange([...value, ...newUniqueFiles].slice(0, maxImages))
  }

  const handleRemoveImage = (idToRemove?: string) => {
    if (!idToRemove) return
    onChange(value.filter((img) => img.id !== idToRemove))
  }

  return (
    <div className="space-y-4">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {value.map((image, index) => (
            <div
              key={image.id || index}
              className="relative size-24 overflow-hidden rounded-md border border-border bg-subdued"
            >
              <img
                src={getFileUrl(image.url || '')}
                alt={`Product image ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label={`Remove image ${index + 1}`}
                className="absolute right-1 top-1 shadow-z1"
                onClick={() => handleRemoveImage(image.id)}
              >
                <X />
              </Button>
            </div>
          ))}

          {value.length < maxImages && (
            <button
              type="button"
              className="flex size-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-input text-xs font-medium text-text-2 transition-colors hover:bg-subdued focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setIsGalleryOpen(true)}
            >
              <Plus className="size-4" aria-hidden="true" />
              Add image
            </button>
          )}
        </div>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>

      <MediaGalleryModal
        open={isGalleryOpen}
        onOpenChange={setIsGalleryOpen}
        onSelect={handleSelectImages}
        multiple={true}
      />
    </div>
  )
}
